/**
 * <s-scroll-dots> — Page dots for a horizontal scroll strip.
 *
 * Wraps a scroller ([data-scroller]) and a dot list ([data-dots]). Builds one
 * dot per visible "page" of the scroller, highlights the current page while
 * scrolling, and scrolls to a page when its dot is clicked. Dots hide when
 * everything fits without scrolling.
 *
 * Element dataset:
 *   data-label — Accessible label template for each dot, e.g. "Go to page [n]".
 */
class SScrollDots extends HTMLElement {
  #scroller;
  #dots;
  #pages = 0;
  #frame = 0;
  #resizeObserver;

  connectedCallback() {
    this.#scroller = this.querySelector('[data-scroller]');
    this.#dots = this.querySelector('[data-dots]');
    if (!this.#scroller || !this.#dots) return;

    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#dots.addEventListener('click', this.#onClick);
    this.#resizeObserver = new ResizeObserver(() => this.#build());
    this.#resizeObserver.observe(this.#scroller);
    this.#build();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#dots?.removeEventListener('click', this.#onClick);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #build() {
    const { scrollWidth, clientWidth } = this.#scroller;
    const pages = clientWidth > 0 ? Math.ceil((scrollWidth - 1) / clientWidth) : 0;
    if (pages === this.#pages) {
      this.#update();
      return;
    }
    this.#pages = pages;
    this.#dots.replaceChildren();
    this.#dots.hidden = pages < 2;
    const label = this.dataset.label || 'Go to page [n]';
    for (let i = 0; i < pages; i++) {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.page = String(i);
      button.setAttribute('aria-label', label.replace('[n]', String(i + 1)));
      button.className = 'flex size-6 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-accent';
      const dot = document.createElement('span');
      dot.className = 'block h-2 w-2 rounded-full bg-on-surface/25 motion-safe:transition-all duration-300';
      dot.setAttribute('aria-hidden', 'true');
      button.append(dot);
      li.append(button);
      this.#dots.append(li);
    }
    this.#update();
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #onClick = (event) => {
    const button = event.target.closest('button[data-page]');
    if (!button) return;
    const page = Number(button.dataset.page);
    const max = this.#scroller.scrollWidth - this.#scroller.clientWidth;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#scroller.scrollTo({ left: Math.min(page * this.#scroller.clientWidth, max), behavior: reduce ? 'auto' : 'smooth' });
  };

  #update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.#scroller;
    const max = scrollWidth - clientWidth;
    const rtlLeft = Math.abs(scrollLeft);
    let current = Math.round(rtlLeft / clientWidth);
    if (rtlLeft >= max - 2) current = this.#pages - 1;
    this.#dots.querySelectorAll('button').forEach((button, index) => {
      const active = index === current;
      button.setAttribute('aria-current', active ? 'true' : 'false');
      const dot = button.firstElementChild;
      dot.classList.toggle('w-6', active);
      dot.classList.toggle('w-2', !active);
      dot.classList.toggle('bg-interactive', active);
      dot.classList.toggle('bg-on-surface/25', !active);
    });
  }
}

if (!customElements.get('s-scroll-dots')) customElements.define('s-scroll-dots', SScrollDots);
